import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useSettings } from '../../context/SettingsContext';
import { formatPhoneNumber } from '../../utils/format';
import SocialLinks from '../common/SocialLinks';
import { buildSocialLinks } from '../../utils/socialLinks';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/profile`);
        if (res.data) {
          setProfile(res.data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setLoading(false);
      }
    };

    fetchProfile();
  }, [apiUrl]);

  // Default contact info if profile is not loaded or doesn't have contact info
  const contactEmail = profile?.contactInfo?.email || settings.contactEmail || 'info@musicianportfolio.com';
  const contactPhone = formatPhoneNumber(profile?.contactInfo?.phone || '(123) 456-7890');
  const contactLocation = profile?.contactInfo?.location || 'City, State';

  const hasSocialLinks = buildSocialLinks(profile?.socialMedia).length > 0;

  // Function to open location in Google Maps
  const openLocationMap = () => {
    const encodedLocation = encodeURIComponent(contactLocation);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="footer">
      <div className="container footer-container">
        <div className="footer-section">
          <h3>{settings.siteName}</h3>
          <p>{settings.siteDescription}</p>
        </div>

        <div className="footer-section">
          <h3>Quick Links</h3>
          <div className="footer-links-grid">
            <div className="footer-links-column">
              <Link to="/" className="footer-link">Home</Link>
              <Link to="/about" className="footer-link">About</Link>
              <Link to="/gallery" className="footer-link">Gallery</Link>
            </div>
            <div className="footer-links-column">
              {settings.enableBookings && (
                <Link to="/bookings" className="footer-link">Bookings</Link>
              )}
              {settings.enableLessons && (
                <Link to="/lessons" className="footer-link">Lessons</Link>
              )}
              <Link to="/contact" className="footer-link">Contact</Link>
            </div>
          </div>
        </div>

        <div className="footer-section">
          <h3>Contact</h3>
          <ul className="footer-contact-list">
            <li className="footer-contact-item">
              <FaEnvelope className="footer-contact-icon" />
              <a href={`mailto:${contactEmail}`} className="footer-contact-link">
                {contactEmail}
              </a>
            </li>
            <li className="footer-contact-item">
              <FaPhone className="footer-contact-icon" />
              <a href={`tel:${contactPhone.replace(/\D/g, '')}`} className="footer-contact-link">
                {contactPhone}
              </a>
            </li>
            <li className="footer-contact-item">
              <FaMapMarkerAlt className="footer-contact-icon" />
              <span 
                className="footer-contact-link location-link" 
                onClick={openLocationMap}
                onKeyPress={(e) => e.key === 'Enter' && openLocationMap()}
                tabIndex="0"
                role="button"
              >
                {contactLocation} <FaExternalLinkAlt size="0.8em" style={{ marginLeft: '5px', verticalAlign: 'middle' }} />
              </span>
            </li>
          </ul>
        </div>

        {hasSocialLinks && (
          <div className="footer-section footer-section--socials">
            <h3>Socials</h3>
            <SocialLinks socialMedia={profile?.socialMedia} />
          </div>
        )}
      </div>

      <div className="footer-bottom">
        <p>&copy; {currentYear} {settings.siteName}. All rights reserved.</p>
      </div>
    </footer>
  );
};

export default Footer; 